import { useSessionContext, logger } from "kmstack-shared";
import { useEffect, useCallback } from "react";
import { AuthService } from "../services/auth.service";

interface Props {
    otherFnc?: (isAuthenticated: boolean) => void;
}

/**
 * A custom hook that handles redirection based on the user's authentication status.
 * 
 * - If the user is authenticated, they are redirected to the previous subdomain
 *   (if the request was made from a different subdomain). Otherwise, they are redirected
 *   to the account page.
 * - If the user is not authenticated, they are redirected to the login page.
 * - It avoids making unnecessary API calls by using the current authentication state from Redux.
 * - The referrer is captured from `document.referrer` and stored in `localStorage` for redirection.
 * 
 * @param {Object} props - The props object.
 * @param {Function} props.dispatch - Redux dispatch function to trigger authentication actions.
 * @param {Function} [props.otherFnc] - Optional callback function that can be executed with the authentication status (true/false).
 * 
 * @returns {Object} - Returns an object with the loading state and a function to set it.
 */
export const useAuthRedirect = ({ otherFnc }: Props) => {
    const { sessionContext, isLoading, isAuthenticated } = useSessionContext();

    const memoizedOtherFnc = useCallback(otherFnc || (() => { }), []);

    useEffect(() => {
        if (isLoading) return;

        if (isAuthenticated) {
            AuthService.redirectOnClient();
            memoizedOtherFnc(true);
            localStorage.removeItem('redirectTo');
        } else {
            memoizedOtherFnc(false);
        }
    }, [isAuthenticated, isLoading]);

    return { loading: isLoading };
};
